import { Key, ReactNode, useState } from 'react';
import { Button } from '@carbon/react';
import styles from '../expand.module.scss';

export interface ExpandColumn<Row> {
  header: ReactNode;
  // Right-align both the header and its cells (used for numeric columns).
  align?: 'right';
  cell: (row: Row) => ReactNode;
}

interface Props<Row> {
  columns: ExpandColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row, index: number) => Key;
  // Optional emphasized summary row rendered under the body (e.g. Grand Total).
  // One cell per column, positionally aligned to `columns`.
  footer?: ReactNode[];
  // When set, render only pageSize rows per page with a compact pager below.
  pageSize?: number;
  // Full-grid look (borders on every cell, darker header, 14px) for the Parties
  // table; the typed cost/line-item tables keep the compact borderless default.
  bordered?: boolean;
}

// The shared line-item / cost table for the typed expands. Centralizes the
// gray-header / borderless-body / hover styling and the right-alignment of
// numeric columns so AFE, DO and RC render identically.
//
// Deliberately a raw table rather than components/data-table: these sub-tables
// are tiny (a cost summary, ~25 line items), need a positional grand-total
// footer row and bespoke borderless styling that DataTable exposes no hook for,
// and don't want its always-on virtualization/toolbar machinery.
export function ExpandTable<Row>({
  columns,
  rows,
  rowKey,
  footer,
  pageSize,
  bordered,
}: Props<Row>) {
  const [page, setPage] = useState(0);
  const alignClass = (column: ExpandColumn<Row>) => {
    return column.align === 'right' ? styles.num : undefined;
  };

  const paginated = !!pageSize && rows.length > pageSize;
  const pageCount = pageSize ? Math.ceil(rows.length / pageSize) : 1;
  // Clamp in case rows shrank since the last render (e.g. a filter changed).
  const safePage = Math.min(page, pageCount - 1);
  const start = paginated ? safePage * pageSize! : 0;
  const visibleRows = paginated ? rows.slice(start, start + pageSize!) : rows;
  // Grand-total row belongs once, after the last row overall — not once per page.
  const isLastPage = !paginated || safePage === pageCount - 1;

  return (
    <div className={styles.tableWrap}>
      <table
        className={[styles.table, bordered ? styles.tableBordered : '']
          .filter(Boolean)
          .join(' ')}
      >
        <thead>
          <tr>
            {columns.map((column, i) => (
              <th key={i} className={alignClass(column)}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, i) => (
            <tr key={rowKey(row, start + i)}>
              {columns.map((column, j) => (
                <td key={j} className={alignClass(column)}>
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
          {footer && isLastPage && (
            <tr className={styles.grandRow}>
              {columns.map((column, j) => (
                <td key={j} className={alignClass(column)}>
                  {footer[j]}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
      {paginated && (
        <div className={styles.pager}>
          <Button
            kind="ghost"
            size="sm"
            disabled={safePage === 0}
            onClick={() => setPage(Math.max(0, safePage - 1))}
          >
            Previous
          </Button>
          <span className={styles.pagerLabel}>
            {start + 1}–{start + visibleRows.length} of {rows.length}
          </span>
          <Button
            kind="ghost"
            size="sm"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
